import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GradFlow } from "gradflow";
import lottie from "lottie-web";

import Footer from "../components/Footer";
import ReportForm from "../components/ReportForm";
import { HERO_GRADFLOW_CONFIG } from "../utils/heroGradient";
import thumbsupanimation from "../components/lottie/thumbsup.json";

/**
 * `/report`. The form used to be a Bootstrap modal opened from Home, Bleeps and
 * the floating button, which meant a five step form with an eight tile photo
 * grid and a full screen map picker lived inside a dialog: it could not be
 * linked to, could not be reloaded without losing your place, and stacked a
 * second modal on top of itself to pin a location.
 *
 * The page owns the shell, the brand band and the success state; ReportForm owns
 * the fields and the submit, and calls `onPosted` when the API accepts.
 */
const ReportPage = ({ domain }) => {
  const [posted, setPosted] = useState(false);
  const lottieContainer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // The thumbs-up animation was imported and its container commented out, so it
  // has been loading a Lottie player and a JSON payload for nothing. This is
  // where it was always meant to go.
  useEffect(() => {
    if (!posted || !lottieContainer.current) return;
    const animation = lottie.loadAnimation({
      container: lottieContainer.current,
      renderer: "svg",
      loop: false,
      autoplay: true,
      animationData: thumbsupanimation,
    });
    return () => animation.destroy();
  }, [posted]);

  // Posting scrolls back to the top: the submit button is at the end of a long
  // form, so the success panel would otherwise appear above the fold and look
  // like the form had simply vanished.
  useEffect(() => {
    if (posted) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [posted]);

  // react-router gives the first entry of a session the key "default", so this
  // is how we know the page was opened cold: going back would leave the site
  // entirely, so fall through to the feed instead. Same rule as the post page.
  const dismiss = () =>
    location.key === "default" ? navigate("/") : navigate(-1);

  return (
    <div className="bleep-light bl-page report-page">
      {/* Behind everything, not just a band at the top. Fixed rather than
          absolute so one viewport-sized canvas serves a page several screens
          tall, and the content scrolls over it. */}
      <GradFlow
        className="report-page__gradflow"
        config={HERO_GRADFLOW_CONFIG}
      />

      <section className="bl-section report-page__body">
        <div className="bl-shell bl-shell--form">
          {/* .bl-on-brand flips the token scale to the off-white set, so no
              colour is named in this subtree. It stops here on purpose: the
              form card below keeps the ordinary light scale, which is what lets
              its white surfaces and dark labels survive sitting on a gradient. */}
          <div className="report-page__head bl-on-brand">
            <div className="bl-head-row">
              <h1 className="report-page__title">
                {posted ? "Report posted" : "Report a counterfeit!"}
              </h1>
              <button
                type="button"
                className="bl-close-btn"
                onClick={dismiss}
                aria-label="Close and go back"
              >
                <i
                  className="fas fa-xmark"
                  aria-hidden="true"
                />
              </button>
            </div>
            <p className="report-page__lead">
              {posted
                ? "Thank you for bleeping. Our team will review it and update the status on your report as it moves along."
                : "Your report will help protect buyers and brands across Africa."}
            </p>
          </div>

          {posted ? (
            <div className="report-done bl-on-brand">
              <div
                className="report-done__art"
                ref={lottieContainer}
                aria-hidden="true"
              />
              <div className="report-done__actions">
                <Link
                  to="/bleeps"
                  className="bleep-btn-report bleep-btn-report--sm"
                >
                  <i
                    className="fa-solid fa-bolt-lightning"
                    aria-hidden="true"
                  />
                  See all bleeps
                </Link>
                <button
                  type="button"
                  className="report-done__again"
                  onClick={() => setPosted(false)}
                >
                  Report something else
                </button>
              </div>
            </div>
          ) : (
            <ReportForm
              domain={domain}
              onPosted={() => setPosted(true)}
            />
          )}
        </div>
      </section>

      <Footer domain={domain} />
    </div>
  );
};

export default ReportPage;
